import React from 'react';
import {
  Box,
  Typography,
  Button,
} from '@mui/material';
import {
  Bolt as FlashIcon,
  EmojiEvents as TrophyIcon,
  Speed as SpeedIcon,
  Groups as GroupsIcon,
  Smartphone as PhoneIcon,
  Timer as TimerIcon,
  Quiz as QuizIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import InstaQuizShell from './InstaQuizShell';
import InstaQuizHeroHeader from './InstaQuizHeroHeader';
import {
  instaQuizPrimaryButtonSx,
  INSTA_QUIZ,
} from './instaQuizTheme';

const EXPECT_ITEMS = [
  {
    icon: <SpeedIcon sx={{ color: INSTA_QUIZ.cyan }} />,
    text: 'Lightning-fast questions drop the moment the host sends them',
  },
  {
    icon: <TrophyIcon sx={{ color: '#FFD54F' }} />,
    text: 'Race the clock, rack up points, and climb the live leaderboard',
  },
  {
    icon: <GroupsIcon sx={{ color: INSTA_QUIZ.purpleLight }} />,
    text: "You're playing alongside music lovers in real time",
  },
  {
    icon: <PhoneIcon sx={{ color: '#81C784' }} />,
    text: "Stay on this screen — we'll keep you in the groove",
  },
];

const InstaQuizJoinWelcome = ({
  sessionName,
  parsedRapid,
  isAuthenticated,
  joining,
  onJoin,
}) => (
  <InstaQuizShell showHeader={false} centered>
    <Box
      sx={{
        width: '100%',
        maxWidth: 900,
        minHeight: 'calc(100vh - 32px)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        py: { xs: 3, sm: 5 },
        px: { xs: 1, sm: 2 },
      }}
    >
      <InstaQuizHeroHeader
        variant="live"
        title="Welcome to Insta Quiz"
        subtitle={
          sessionName
            ? `${sessionName} is live — your moment to flex your music knowledge and steal the spotlight.`
            : 'A live round is waiting for you. Jump in, trust your ears, and chase the top of the board.'
        }
      />

      {/* What to expect */}
      <Box sx={{ mb: 3, maxWidth: 640, mx: 'auto', width: '100%' }}>
        <Typography
          variant="overline"
          sx={{
            color: INSTA_QUIZ.purpleLight,
            fontWeight: 700,
            letterSpacing: 2,
            display: 'block',
            mb: 2,
            textAlign: 'center',
          }}
        >
          YOUR PLAYBOOK
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {EXPECT_ITEMS.map((item, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                p: 1.75,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <Box sx={{ mt: 0.25, flexShrink: 0 }}>{item.icon}</Box>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.6 }}>
                {item.text}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Session stats */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
          mb: 4,
          p: 2.5,
          borderRadius: 3,
          maxWidth: 640,
          mx: 'auto',
          width: '100%',
          background: 'rgba(156,39,176,0.08)',
          border: `1px solid ${INSTA_QUIZ.cardBorder}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TimerIcon sx={{ color: INSTA_QUIZ.purpleLight }} />
          <Box>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', display: 'block' }}>
              Quizlet timer
            </Typography>
            <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600 }}>
              {parsedRapid.song_timer_seconds || 60} seconds per round
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <QuizIcon sx={{ color: INSTA_QUIZ.purpleLight }} />
          <Box>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', display: 'block' }}>
              Questions per quizlet
            </Typography>
            <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600 }}>
              {parsedRapid?.questions_per_song ?? '—'} questions
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* CTA */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
        <Button
          variant="contained"
          size="large"
          onClick={onJoin}
          disabled={joining}
          startIcon={!isAuthenticated ? <LoginIcon /> : <FlashIcon />}
          sx={{
            ...instaQuizPrimaryButtonSx,
            minWidth: 280,
            fontSize: '1.15rem',
            py: 2,
          }}
        >
          {joining
            ? 'Getting you in…'
            : isAuthenticated
              ? "I'm Ready — Let's Play!"
              : 'Sign In & Join the Fun'}
        </Button>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)' }}>
          One tap and you&apos;re in the game. Good luck, superstar.
        </Typography>
      </Box>
    </Box>
  </InstaQuizShell>
);

export default InstaQuizJoinWelcome;
